import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bot,
  Building2,
  CalendarClock,
  ClipboardList,
  Link2,
 Loader2,  Mail,
  NotebookPen,
  Phone,
  Pin } from 'lucide-react';
import React, { useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type RightPanelType = 'notes-activity' | 'tasks' | null;

interface ActionButton {
  id: RightPanelType;
  icon: React.ElementType;
  label: string;
  description: string;
}

interface MatterRightActionBarProps {
  activePanel: RightPanelType;
  onPanelChange: (panel: RightPanelType) => void;
  onOpenAssistant: () => void;
  onOpenDialpad: () => void;
  onOpenSendEmail: () => void;
  onOpenPropertyLookup: () => void;
  /** Should return the portal URL string, or throw on failure */
  onCopyPortalLink: () => Promise<string>;
  onOpenScheduleMeeting: () => void;
  onOpenEscalate: () => void;
  isEscalateDisabled?: boolean;
  hasPinnedNotes?: boolean;
  isPinnedNotesExpanded?: boolean;
  onTogglePinnedNotes?: () => void;
  className?: string;
}

const panelButtons: ActionButton[] = [
  {
    id: 'tasks',
    icon: ClipboardList,
    label: 'Tasks',
    description: 'View and manage tasks',
  },
  {
    id: 'notes-activity',
    icon: NotebookPen,
    label: 'Notes & Activity',
    description: 'View notes and activity',
  },
];

export const MatterRightActionBar: React.FC<MatterRightActionBarProps> = ({
  activePanel,
  onPanelChange,
  onOpenAssistant,
  onOpenDialpad,
  onOpenSendEmail,
  onOpenPropertyLookup,
  onCopyPortalLink,
  onOpenScheduleMeeting,
  onOpenEscalate,
  isEscalateDisabled = false,
  hasPinnedNotes = false,
  isPinnedNotesExpanded = false,
  onTogglePinnedNotes,
  className,
}) => {
  const handlePanelClick = (panelId: RightPanelType) => {
    if (activePanel === panelId) {
      onPanelChange(null);
    } else {
      onPanelChange(panelId);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          'flex flex-col items-center gap-1.5 py-3 px-1.5 h-full',
          className,
        )}
      >
        {/* Pinned notes button — appears with scale animation when notes are pinned */}
        <AnimatePresence>
          {hasPinnedNotes && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onTogglePinnedNotes}
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm relative',
                      isPinnedNotesExpanded
                        ? 'bg-amber-400 text-amber-950 shadow-md'
                        : 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200',
                    )}
                  >
                    <Pin className="h-3.5 w-3.5 fill-current" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" sideOffset={6}>
                  <p className="text-xs font-medium">
                    {isPinnedNotesExpanded
                      ? 'Minimize pinned notes'
                      : 'Show pinned notes'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Panel toggle buttons (radio pattern) */}
        {panelButtons.map((button) => {
          const Icon = button.icon;
          const isActive = activePanel === button.id;

          return (
            <Tooltip key={button.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handlePanelClick(button.id)}
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm relative',
                    isActive
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-white text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700 border border-zinc-200',
                  )}
                >
                  <Icon className="h-4 w-4 fill-none" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={6}>
                <p className="text-xs font-medium">{button.label}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Divider */}
        <div className="w-5 border-t border-zinc-200 my-0.5" />

        {/* Quick action buttons — unified neutral style */}
        {[
          { icon: Mail, label: 'Send Email', onClick: onOpenSendEmail },
          { icon: Phone, label: 'Make Call', onClick: onOpenDialpad },
          {
            icon: Building2,
            label: 'Property Lookup',
            onClick: onOpenPropertyLookup,
          },
        ].map((action) => {
          const ActionIcon = action.icon;
          return (
            <Tooltip key={action.label}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={action.onClick}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm bg-white text-zinc-500 hover:bg-primary/5 hover:text-primary border border-zinc-200"
                >
                  <ActionIcon className="h-4 w-4 fill-none" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={6}>
                <p className="text-xs font-medium">{action.label}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Copy Secure Upload Link — with loader + popover */}
        <PortalLinkButton onCopyPortalLink={onCopyPortalLink} />

        {/* Schedule Meeting button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onOpenScheduleMeeting}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm bg-white text-zinc-500 hover:bg-primary/5 hover:text-primary border border-zinc-200"
            >
              <CalendarClock className="h-4 w-4 fill-none" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={6}>
            <p className="text-xs font-medium">Schedule Meeting</p>
          </TooltipContent>
        </Tooltip>

        {/* Divider */}
        <div className="w-5 border-t border-zinc-200 my-0.5" />

        {/* FinchGPT Assistant button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onOpenAssistant}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm bg-white text-zinc-500 hover:bg-primary/5 hover:text-primary border border-zinc-200"
            >
              <Bot className="h-4 w-4 fill-none" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={6}>
            <p className="text-xs font-medium">FinchGPT</p>
          </TooltipContent>
        </Tooltip>

        {/* Escalate Case button — only exception: subtle warm tone for destructive action */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={isEscalateDisabled ? undefined : onOpenEscalate}
              disabled={isEscalateDisabled}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm border',
                isEscalateDisabled
                  ? 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed'
                  : 'bg-white text-zinc-500 hover:bg-amber-50 hover:text-amber-600 border-zinc-200',
              )}
            >
              <AlertTriangle className="h-4 w-4 fill-none" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={6}>
            <p className="text-xs font-medium">
              {isEscalateDisabled
                ? 'Escalation unavailable - case is still in initial review'
                : 'Escalate Case'}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

/** Portal link button with loader spinner and popover showing the URL */
const PortalLinkButton: React.FC<{
  onCopyPortalLink: () => Promise<string>;
}> = ({ onCopyPortalLink }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [showPopover, setShowPopover] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    setPortalUrl(null);
    setShowPopover(true);
    try {
      const url = await onCopyPortalLink();
      setPortalUrl(url);
    } catch {
      setShowPopover(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover open={showPopover} onOpenChange={setShowPopover}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={handleClick}
              disabled={isLoading}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm bg-white text-zinc-500 hover:bg-primary/5 hover:text-primary border border-zinc-200 disabled:opacity-70"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 fill-none" />
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        {!showPopover && (
          <TooltipContent side="left" sideOffset={6}>
            <p className="text-xs font-medium">Copy Secure Upload Link</p>
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent
        side="left"
        sideOffset={6}
        className="w-72 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1.5">
          Upload Link
        </p>
        {portalUrl ? (
          <input
            readOnly
            value={portalUrl}
            onFocus={(e) => e.target.select()}
            className="w-full text-xs font-mono bg-zinc-50 border border-zinc-200 rounded-lg px-2.5 py-2 text-zinc-700 select-all"
          />
        ) : (
          <div className="flex items-center gap-2 py-2 text-xs text-zinc-400">
            <Loader2 className="size-3 animate-spin" />
            Generating link...
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MatterRightActionBar;
